import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;
type PathProps = SVGProps<SVGPathElement>;

export interface LogoProps {
  svg?: IconProps;
  path?: PathProps;
}

export function CoderLogo(props: Readonly<LogoProps>) {
  const { svg, path } = props;

  return (
    <svg
      width="65"
      height="65"
      viewBox="0 0 65 65"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...svg}
    >
      <path
        d="M31.1648 43.2927L36.0087 30.351M40.4896 47.1832C40.4896 48.3343 39.9145 48.9094 38.7644 48.9085H36.1752C34.8337 48.9085 33.9711 49.4889 33.5874 50.6499C33.2046 49.4889 32.3416 48.9085 30.9982 48.9085H28.3969C27.2458 48.9085 26.6703 48.3334 26.6703 47.1832M28.3955 33.3626L21.4933 35.9518L28.3183 38.5545M38.7644 33.3626L45.6815 35.9518L38.7644 38.5545"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...path}
      />
      <path
        d="M16.3001 26.4455C24.9438 29.9122 31.8608 28.1856 38.7644 22.9937C38.7644 25.2958 38.1889 27.0265 37.0378 28.1856C42.231 29.9108 45.6815 24.719 49.9972 24.719C51.1464 24.719 52.5891 25.2945 54.3251 26.4455C54.9002 24.1434 54.3201 22.4178 52.585 21.2685C52.585 18.9665 53.7406 17.2408 56.0517 16.0916C53.7406 14.9315 51.434 15.5066 49.1319 17.8168C49.1319 14.3501 45.6815 10.8997 42.231 9.17313C43.3812 10.3242 43.9563 12.0498 43.9563 14.3501C37.0365 7.44792 31.8622 7.44792 24.9438 7.44792C26.669 8.59806 27.8196 9.74865 28.3955 10.8997C21.492 10.8997 16.3001 14.3501 9.39657 7.44656C7.67271 17.8168 11.1231 22.9937 16.3001 26.4455ZM16.3001 26.4455V35.0878C16.3001 37.3989 15.15 38.5545 12.8497 38.5545C15.15 38.5545 16.3001 39.7051 16.3001 42.0063V54.1003C16.3001 56.4024 18.0307 57.553 21.492 57.5521"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...path}
      />
      <path
        d="M45.6815 57.5521C47.9835 57.5521 49.1341 56.4015 49.1332 54.1003V42.0063C49.1332 39.7042 50.2834 38.5536 52.5836 38.5545C50.2834 38.5545 49.1332 37.3989 49.1332 35.0878V25.067"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...path}
      />
    </svg>
  );
}

export function FilledCoderLogo(
  props: LogoProps & { hair?: PathProps; color?: string },
) {
  const { svg, path, color, hair } = props;

  return (
    <svg
      width="65"
      height="65"
      viewBox="0 0 65 65"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...svg}
    >
      <path
        d="M30.1648 44.2918L35.0087 31.35M39.4896 48.1823C39.4896 49.3333 38.9145 49.9084 37.7644 49.9075H35.1752C33.8337 49.9075 32.9711 50.488 32.5874 51.649C32.2046 50.488 31.3416 49.9075 29.9982 49.9075H27.3969C26.2458 49.9075 25.6703 49.3324 25.6703 48.1823M27.3955 34.3617L20.4933 36.9508L27.3183 39.5535M37.7644 34.3617L44.6815 36.9508L37.7644 39.5535"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...path}
      />
      <path
        className={'hair-path'}
        d="M38.7644 22.9937C31.8608 28.1856 24.9438 29.9122 16.3001 26.4455C11.1231 22.9937 9.39658 7.44656 9.39658 7.44656C9.39658 7.44656 21.492 10.8997 28.3955 10.8997C27.8196 9.74865 26.669 8.59806 24.9438 7.44792C31.8622 7.44792 37.0365 7.44792 43.9563 14.3501C43.9563 12.0498 42.2311 9.17313 42.2311 9.17313C42.2311 9.17313 49.1319 14.3501 49.1319 17.8168C51.434 15.5066 56.0517 16.0916 56.0517 16.0916C56.0517 16.0916 52.585 18.9665 52.585 21.2685C54.3202 22.4178 54.3251 26.4455 54.3251 26.4455L49.9972 24.719C45.6815 24.719 37.0378 28.1856 37.0378 28.1856C37.0378 28.1856 38.7644 25.2958 38.7644 22.9937Z"
        fill={color ?? 'black'}
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...path}
        {...hair}
      />
      <path
        d="M16.3 54C16.3 53.0792 16.3 47.4 16.3 47.4C16.3 46.6752 16.3 46.3311 16.3 45.8V42"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...path}
      />
      <path
        d="M16.3 35C16.3 34.3861 16.3 30.6 16.3 30.6C16.3 30.1168 16.3 29.8874 16.3 29.5333V27"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...path}
      />
      <path
        d="M46 57.4851C48.3021 57.4851 49.4527 56.3345 49.4518 54.0333V41.9393C49.4518 39.6372 50.6019 38.4866 52.9022 38.4875C50.6019 38.4875 49.4518 37.3319 49.4518 35.0208C49.4518 32.7097 49.4518 25 49.4518 25"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...path}
      />
      <path
        d="M16.2982 54C16.2982 56.3021 17.4488 57.4527 19.75 57.4518"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...path}
      />
      <path
        d="M16.3 35C16.3 37.3021 15.1498 38.4527 12.8496 38.4518"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...path}
      />
      <path
        d="M12.8496 38.5451C15.1498 38.5451 16.3 39.7007 16.3 42.0118"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...path}
      />
    </svg>
  );
}

export function BlueFilledCoderLogo(
  props: LogoProps & { hair?: PathProps; color?: string },
) {
  const { svg, path, color, hair } = props;

  return (
    <svg
      width="52"
      height="52"
      viewBox="0 0 52 52"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...svg}
    >
      <g clipPath="url(#clip0_2683_27)">
        <path
          d="M21.1648 37.2918L26.0087 24.35M18.3955 27.3617L11.4933 29.9508L18.3183 32.5535M28.7644 27.3617L35.6815 29.9508L28.7644 32.5535"
          stroke="#4F92E3"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          {...path}
        />
        <path
          d="M30.4896 42.1823C30.4896 43.3333 29.9145 43.9084 28.7644 43.9075H26.1752C24.8337 43.9075 23.9711 44.488 23.5874 45.649C23.2046 44.488 22.3416 43.9075 20.9982 43.9075C19.6549 43.9075 19.5479 43.9075 18.3969 43.9075C17.2458 43.9075 16.6703 43.3324 16.6703 42.1823"
          stroke="#4F92E3"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          {...path}
        />
        <path
          className={'hair-path'}
          d="M30.3644 15.9937C30.3644 15.7663 30.2358 15.5585 30.0324 15.4569C29.8289 15.3554 29.5855 15.3775 29.4038 15.5142C26.0021 18.0724 22.6333 19.7495 19.0806 20.3757C15.5505 20.9979 11.7927 20.5905 7.58287 18.9124C6.45084 18.1344 5.44669 16.6568 4.58291 14.7709C3.71447 12.8747 3.0239 10.6471 2.48909 8.50621C1.95507 6.36852 1.58062 4.33496 1.33965 2.8346C1.23876 2.20643 1.16141 1.67258 1.10562 1.26472C1.41449 1.34833 1.79772 1.45063 2.243 1.56671C3.48997 1.89179 5.22493 2.32526 7.17713 2.75885C11.0659 3.62257 15.872 4.49969 19.3955 4.49969C19.6035 4.49969 19.7967 4.39198 19.906 4.21505C20.0153 4.03811 20.0252 3.81719 19.9321 3.63119C19.4879 2.74353 18.7549 1.88616 17.7623 1.05305C20.4704 1.07601 22.8806 1.20154 25.2849 1.88766C28.1924 2.71737 31.1302 4.38121 34.5325 7.77491C34.7043 7.94619 34.9622 7.99724 35.1862 7.90429C35.4102 7.81133 35.5563 7.59264 35.5563 7.35011C35.5563 6.34556 35.2637 5.27239 34.9247 4.36053C35.3474 4.73889 35.8025 5.16387 36.2569 5.61892C37.1054 6.46854 37.9377 7.40914 38.554 8.33595C39.1809 9.27856 39.5319 10.1298 39.5319 10.8168C39.5319 11.0596 39.6783 11.2786 39.9028 11.3713C40.1272 11.464 40.3855 11.4123 40.5569 11.2403C41.5582 10.2355 43.1141 9.82329 44.5141 9.68493C44.9042 9.64637 45.273 9.62997 45.601 9.62628C45.3689 9.86201 45.1166 10.1316 44.8637 10.4253C44.4145 10.9469 43.9527 11.5573 43.6005 12.2002C43.2519 12.8364 42.985 13.5508 42.985 14.2685C42.985 14.4698 43.0859 14.6576 43.2537 14.7688C43.5567 14.9695 43.8222 15.3242 44.0425 15.81C44.2602 16.2904 44.4122 16.8511 44.5162 17.3998C44.5945 17.8125 44.6436 18.2076 44.6743 18.5399L41.2195 17.1617C41.1488 17.1335 41.0733 17.119 40.9972 17.119C38.7331 17.119 35.4093 18.0165 32.7158 18.881C31.3745 19.3115 30.1662 19.7416 29.2857 20.0672C29.3408 19.9463 29.3968 19.8199 29.4528 19.6887C29.8927 18.6594 30.3644 17.2698 30.3644 15.9937Z"
          fill={color ?? '#48C8FF'}
          fillOpacity="0.32"
          stroke="#4F92E3"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          {...path}
          {...hair}
        />
        <path
          d="M37 50.4851C39.3021 50.4851 40.4527 49.3345 40.4518 47.0333V34.9393C40.4518 32.6372 41.6019 31.4866 43.9022 31.4875C41.6019 31.4875 40.4518 30.3319 40.4518 28.0208C40.4518 25.7097 40.4518 18 40.4518 18"
          stroke="#4F92E3"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          {...path}
        />
        <path
          d="M6.3 46C6.3 46.5523 6.74771 47 7.3 47C7.85228 47 8.3 46.5523 8.3 46H6.3ZM8.3 34C8.3 33.4477 7.85228 33 7.3 33C6.74771 33 6.3 33.4477 6.3 34H8.3ZM8.3 19.5V18.5H6.3V19.5H8.3ZM8.29823 46C8.29823 45.4477 7.85051 45 7.29823 45C6.74594 45 6.29823 45.4477 6.29823 46H8.29823ZM10.7504 50.4518C11.3027 50.4516 11.7502 50.0037 11.75 49.4514C11.7498 48.8991 11.3019 48.4516 10.7496 48.4518L10.7504 50.4518ZM3.84997 29.4518C3.29769 29.4516 2.8498 29.8991 2.84958 30.4514C2.84936 31.0037 3.2969 31.4516 3.84919 31.4518L3.84997 29.4518ZM3.84958 29.5451C3.29729 29.5451 2.84958 29.9928 2.84958 30.5451C2.84958 31.0974 3.29729 31.5451 3.84958 31.5451V29.5451ZM6.3 34.0118C6.3 34.5641 6.74771 35.0118 7.3 35.0118C7.85228 35.0118 8.3 34.5641 8.3 34.0118H6.3ZM6.29823 46C6.29823 47.3051 6.62502 48.4676 7.45406 49.2966C8.28317 50.1257 9.44559 50.4523 10.7504 50.4518L10.7496 48.4518C9.75322 48.4522 9.18975 48.2039 8.86828 47.8824C8.54673 47.5608 8.29823 46.997 8.29823 46H6.29823ZM6.3 27C6.3 27.9971 6.05156 28.561 5.73014 28.8825C5.40888 29.2039 4.8458 29.4522 3.84997 29.4518L3.84919 31.4518C5.15364 31.4523 6.31577 31.1257 7.14464 30.2965C7.97337 29.4674 8.3 28.305 8.3 27H6.3ZM3.84958 31.5451C4.84438 31.5451 5.40707 31.7941 5.72862 32.1172C6.05104 32.4412 6.3 33.0092 6.3 34.0118H8.3C8.3 32.7033 7.97388 31.538 7.14616 30.7063C6.31758 29.8739 5.15505 29.5451 3.84958 29.5451V31.5451ZM6.3 34V46H8.3V34H6.3ZM6.3 19.5V27H8.3V19.5H6.3Z"
          fill="#4F92E3"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          {...path}
        />
      </g>
      {/*<defs>*/}
      {/*  <clipPath id="clip0_2683_27">*/}
      {/*    <rect width="48" height="52" fill="white" />*/}
      {/*  </clipPath>*/}
      {/*</defs>*/}
    </svg>
  );
}

export const TmLogo = (props: Readonly<LogoProps>) => {
  return (
    <svg
      width="18"
      height="11"
      viewBox="0 0 18 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props.svg}
    >
      <path
        d="M0.24 1.348C0.256 1.036 0.288 0.796 0.336 0.628C1.2 0.772 2.236 0.844 3.444 0.844C4.652 0.844 5.916 0.7 7.236 0.412C7.1 1.428 6.584 1.984 5.688 2.08C5.408 2.12 4.976 2.16 4.392 2.2C4.376 2.432 4.244 3.448 3.996 5.248C3.748 7.04 3.624 8.276 3.624 8.956C3.624 9.636 3.68 10.108 3.792 10.372C3.112 10.196 2.648 9.964 2.4 9.676C2.152 9.38 2.028 9.016 2.028 8.584C2.028 8.152 2.128 7.216 2.328 5.776C2.536 4.328 2.66 3.164 2.7 2.284C2.052 2.316 1.416 2.332 0.792 2.332C0.424 2.332 0.24 2.004 0.24 1.348ZM13.3916 7.744C13.1036 7.856 12.8796 7.912 12.7196 7.912C12.2396 7.912 11.9716 7.844 11.9156 7.708C11.1156 5.652 10.5876 4.328 10.3316 3.736C10.3076 3.952 10.2436 4.452 10.1396 5.236C9.92362 6.772 9.81562 8.048 9.81562 9.064C9.81562 9.312 9.83162 9.644 9.86363 10.06C9.30362 10.06 8.92362 9.996 8.72362 9.868C8.53162 9.732 8.43562 9.544 8.43562 9.304C8.43562 9.224 8.56762 8.136 8.83163 6.04C9.10363 3.944 9.23963 2.484 9.23963 1.66V1.6C9.00762 1.224 8.76362 0.916 8.50762 0.676C8.80362 0.468 9.15962 0.364 9.57563 0.364C10.1036 0.364 10.5116 0.612 10.7996 1.108C11.0876 1.596 11.6516 2.944 12.4916 5.152L13.0436 6.568C13.0916 6.488 13.1636 6.38 13.2596 6.244C15.2116 3.356 16.2156 1.396 16.2716 0.364C16.2716 0.372 16.3676 0.384 16.5596 0.4C16.7516 0.408 16.9556 0.448 17.1716 0.52C17.3956 0.583999 17.5556 0.687999 17.6516 0.831999C17.7556 0.967999 17.8196 1.104 17.8436 1.24C17.8676 1.368 17.8796 1.592 17.8796 1.912C17.8796 2.224 17.7476 3.284 17.4836 5.092C17.2196 6.9 17.0876 8.168 17.0876 8.896C17.0876 9.616 17.1436 10.108 17.2556 10.372C16.5756 10.196 16.1116 9.964 15.8636 9.676C15.6156 9.38 15.4916 8.972 15.4916 8.452C15.4916 8.148 15.5716 7.4 15.7316 6.208C15.8996 5.016 16.0156 4.064 16.0796 3.352C15.7596 3.904 15.3036 4.66 14.7116 5.62C14.1276 6.58 13.7756 7.152 13.6556 7.336C13.5356 7.512 13.4476 7.648 13.3916 7.744Z"
        fill="currentColor"
        {...props.path}
      />
    </svg>
  );
};
